import { apiDelete, apiGet, apiPatch, apiPost } from '@/api/axios-client'

/**
 * Google Calendar, linked once and kept linked.
 *
 * The browser asks Google (Identity Services, code model) for a one-time authorization code on
 * the same Google client Firebase sign-in uses, and hands it to the API. The API keeps the
 * refresh token encrypted server-side and reads events on the person's behalf, so the link lasts
 * across browsers and devices until they disconnect it here or remove TapWork in their Google
 * account. Nothing calendar-related is stored in the browser.
 */

export interface CalendarLinkStatus {
  available: boolean
  clientId: string | null
  scope: string
  connected: boolean
  email: string | null
  connectedAt: string | null
  /** Google ended the link (revoked or expired); connecting again fixes it. */
  lapsed?: boolean
  /** Tapwork may add, move and cancel events (bookings). Links made before booking existed need one reconnect. */
  canWrite?: boolean
}

export class CalendarError extends Error {
  constructor(
    message: string,
    readonly kind: 'retry' | 'api-disabled' | 'denied' | 'popup' | 'unavailable' | 'other',
    readonly link?: string,
  ) {
    super(message)
  }
}

// Leftovers from the hour-long browser token this replaced.
try {
  sessionStorage.removeItem('tapwork:gcal:token:v1')
  localStorage.removeItem('tapwork:gcal:connected:v1')
} catch {
  // Storage optional.
}

export const fetchLinkStatus = () => apiGet<CalendarLinkStatus>('calendar/google')

// --- Google Identity Services ------------------------------------------------------------------

interface CodeResponse { code?: string; error?: string; error_description?: string }
interface CodeClient { requestCode: () => void }
interface Gis {
  accounts: { oauth2: { initCodeClient: (config: Record<string, unknown>) => CodeClient } }
}

let gisPromise: Promise<Gis> | null = null

/** Loads Google's sign-in library once. Called early so the popup opens inside the click. */
export function loadGis(): Promise<Gis> {
  const w = window as unknown as { google?: Gis }
  if (w.google?.accounts?.oauth2) return Promise.resolve(w.google)
  gisPromise ??= new Promise<Gis>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.onload = () => (w.google?.accounts?.oauth2 ? resolve(w.google) : reject(new Error('gis')))
    script.onerror = () => {
      gisPromise = null
      reject(new CalendarError('Couldn’t reach Google. Check your connection and try again.', 'other'))
    }
    document.head.appendChild(script)
  })
  return gisPromise
}

function requestCode(gis: Gis, link: CalendarLinkStatus, email: string | null): Promise<string> {
  return new Promise((resolve, reject) => {
    const client = gis.accounts.oauth2.initCodeClient({
      client_id: link.clientId,
      scope: `openid email ${link.scope}`,
      ux_mode: 'popup',
      include_granted_scopes: false,
      // Always ask: Google only hands out a refresh token on a consent screen, and without one the link cannot last.
      prompt: 'consent',
      ...(email ? { login_hint: email } : {}),
      callback: (res: CodeResponse) => {
        if (res.code) resolve(res.code)
        else if (res.error === 'access_denied') reject(new CalendarError('Calendar access wasn’t granted.', 'denied'))
        else reject(new CalendarError(res.error_description || 'Google didn’t grant calendar access. Try again.', 'other'))
      },
      error_callback: (err: { type?: string }) => {
        if (err.type === 'popup_failed_to_open') reject(new CalendarError('Your browser blocked the Google window. Allow pop-ups for TapWork and try again.', 'popup'))
        else reject(new CalendarError('The Google window was closed before access was granted.', 'denied'))
      },
    })
    client.requestCode()
  })
}

/**
 * Opens Google's consent window and links the calendar. `link` should already be loaded (it is
 * fetched with the page) so the window opens within the click; browsers block late popups.
 */
export async function connectGoogleCalendar(link: CalendarLinkStatus | undefined, email: string | null): Promise<CalendarLinkStatus> {
  const current = link ?? (await fetchLinkStatus())
  if (!current.available || !current.clientId) throw new CalendarError('Google Calendar isn’t available right now. Try again later.', 'unavailable')
  const code = await requestCode(await loadGis(), current, email)
  try {
    return await apiPost<CalendarLinkStatus>('calendar/google/connect', { code })
  } catch (err) {
    const data = (err as { response?: { data?: { error?: { message?: string; details?: { reason?: string } }; message?: string; details?: { reason?: string } } } }).response?.data
    const reason = data?.error?.details?.reason ?? data?.details?.reason
    const message = data?.error?.message ?? data?.message
    if (reason === 'consent_required') throw new CalendarError(message ?? 'Almost there. Click Connect once more to confirm with Google.', 'retry')
    throw new CalendarError(message ?? 'Couldn’t connect Google Calendar. Try again.', reason === 'scope_missing' ? 'denied' : 'other')
  }
}

export const disconnectGoogleCalendar = () => apiDelete<CalendarLinkStatus>('calendar/google')

export interface CalendarEvent {
  id: string
  title: string
  start: string
  end: string
  allDay: boolean
  location: string | null
  /** A video link (Meet, Zoom, Teams) found on the event, if any. */
  joinUrl: string | null
  htmlLink: string | null
  attendees: number
  organizer: string | null
  responseStatus: 'accepted' | 'declined' | 'tentative' | 'needsAction' | null
  color: string | null
  /** The person organised it, so they may change it from Tapwork. */
  mine: boolean
  description: string | null
}

interface GoogleEvent {
  id: string
  summary?: string
  status?: string
  start?: { dateTime?: string; date?: string }
  end?: { dateTime?: string; date?: string }
  location?: string
  hangoutLink?: string
  htmlLink?: string
  description?: string
  attendees?: Array<{ self?: boolean; responseStatus?: CalendarEvent['responseStatus'] }>
  organizer?: { displayName?: string; email?: string; self?: boolean }
  conferenceData?: { entryPoints?: Array<{ entryPointType?: string; uri?: string }> }
}

const VIDEO_LINK = /https:\/\/(?:meet\.google\.com|[\w.-]*zoom\.us|teams\.microsoft\.com|teams\.live\.com)\/[^\s"<>)]+/i

/** Google's raw event -> what TapWork shows. Pure, exported for tests. */
export function toCalendarEvent(e: GoogleEvent): CalendarEvent | null {
  if (e.status === 'cancelled') return null
  const allDay = Boolean(e.start?.date && !e.start?.dateTime)
  const start = e.start?.dateTime ?? (e.start?.date ? `${e.start.date}T00:00:00` : null)
  const end = e.end?.dateTime ?? (e.end?.date ? `${e.end.date}T00:00:00` : null)
  if (!start || !end) return null
  const video = e.hangoutLink
    ?? e.conferenceData?.entryPoints?.find((p) => p.entryPointType === 'video')?.uri
    ?? e.location?.match(VIDEO_LINK)?.[0]
    ?? e.description?.match(VIDEO_LINK)?.[0]
    ?? null
  const self = e.attendees?.find((a) => a.self)
  return {
    id: e.id,
    title: e.summary?.trim() || '(No title)',
    start,
    end,
    allDay,
    location: e.location && !VIDEO_LINK.test(e.location) ? e.location : null,
    joinUrl: video,
    htmlLink: e.htmlLink ?? null,
    attendees: e.attendees?.length ?? 0,
    organizer: e.organizer?.displayName ?? e.organizer?.email ?? null,
    responseStatus: self?.responseStatus ?? null,
    color: null,
    mine: Boolean(e.organizer?.self),
    description: e.description ?? null,
  }
}

type EventsResult =
  | { status: 'ok'; items: GoogleEvent[] }
  | { status: 'disconnected' }
  | { status: 'api-disabled'; message: string; link: string | null }
  | { status: 'error'; message: string }

/** Thrown when the link ended on Google's side (revoked, password change); the UI re-checks status. */
export class CalendarDisconnected extends Error {}

/** Events on the primary calendar between two instants, expanded (recurring -> instances), via the API. */
export async function fetchEvents(timeMin: Date, timeMax: Date, signal?: AbortSignal): Promise<CalendarEvent[]> {
  const res = await apiGet<EventsResult>('calendar/google/events', { timeMin: timeMin.toISOString(), timeMax: timeMax.toISOString() }, { signal })
  if (res.status === 'disconnected') throw new CalendarDisconnected('Google Calendar was disconnected.')
  if (res.status === 'api-disabled') throw new CalendarError(res.message, 'api-disabled', res.link ?? undefined)
  if (res.status === 'error') throw new CalendarError(res.message, 'other')
  return res.items.map(toCalendarEvent).filter((e): e is CalendarEvent => Boolean(e))
}

export interface EventDraft {
  summary: string
  start: string
  end: string
  description?: string | null
  location?: string | null
  attendees?: Array<{ email: string; displayName?: string | null }>
  meet?: boolean
  timeZone?: string
  /** Google emails the guests. */
  notify?: boolean
}
/** Changes to the person's own calendar, through the API and their own link. */
export const createMyEvent = (e: EventDraft) => apiPost<GoogleEvent>('calendar/google/events', e)
export const updateMyEvent = (id: string, e: Partial<EventDraft>) => apiPatch<GoogleEvent>(`calendar/google/events/${encodeURIComponent(id)}`, e)
export const deleteMyEvent = (id: string, notify = true) => apiDelete<{ ok: true }>(`calendar/google/events/${encodeURIComponent(id)}${notify ? '' : '?notify=0'}`)
